import { createContext } from 'preact-compat'
import { ContentType } from '@insided/shared/utils/contentType'
import {
  Author,
  AuthorFromServer,
  Config,
  Reply,
  GroupedItemsList,
  Item,
  Category,
  Group,
  Categories,
  FORUM_TYPE
} from './interfaces'
import { ParentCategory } from '@insided/control/uikit/CategorySelector'
import { zip } from '@insided/shared/utils/zip'

export function showEscalateButton(
  escalateTopic: boolean,
  escalateReply: boolean,
  isReply: boolean,
  hasUrl: boolean,
  contentType: ContentType
) {
  if (hasUrl) {
    return false
  }
  if (isReply) {
    return true
    // TODO: replace return when new topic moderation page is used for all content types
    // return escalateReply
  }
  return !(contentType === 'article' || contentType === 'productUpdate')
  // TODO: replace return when new topic moderation page is used for all content types
  // return escalateTopic
}

export function getFormattedTitle(title: string = '', isReply: boolean = false) {
  const formattedTitle = isReply ? `RE: ${title ?? ''}` : title

  return formattedTitle.length > 150 ? formattedTitle.slice(0, 147) + '...' : formattedTitle
}

export function getFormattedContent(
  contentType: ContentType,
  destinationUrl: string,
  topicId: number,
  content?: string | null
) {
  const formattedContentType = contentTypeMapping[contentType]
  let url = destinationUrl
  if (['productUpdate', 'idea'].includes(contentType)) {
    url = `${destinationUrl}${formattedContentType}/${topicId}`
  }

  return `${content ?? ''} \n \n------------------\n${url} `
}

export function getAllVisibleReplies(replies: Reply[]) {
  const filteredReplies = replies.filter((reply: Reply) => !reply.trashed && !reply.spam)
  return filteredReplies.map((reply: Reply) => reply.id)
}

function getParentCategories(categories: Categories): ParentCategory[] {
  return categories.filter((category) => {
    const isParentCategory = category.type === FORUM_TYPE.COMMUNITY || category.type === FORUM_TYPE.KNOWLEDGE_BASE
    const hasCategories = 'categories' in category
    return isParentCategory && hasCategories
  })
}

function getGroups(categories: Categories): Group[] {
  return categories.filter((category) => {
    return (
      category.type === FORUM_TYPE.PUBLIC_GROUP ||
      category.type === FORUM_TYPE.PRIVATE_GROUP ||
      category.type === FORUM_TYPE.HIDDEN_GROUP
    )
  })
}

export function getItemsForCategory(categories: Category[]) {
  return categories.map(({ categoryId, categoryName }: Category) => {
    return {
      id: String(categoryId),
      title: categoryName,
      value: String(categoryId)
    }
  })
}

export function getItemsForGroups(groups: Group[]) {
  return groups.map(({ groupId, groupName }: Group) => {
    return {
      id: String(groupId),
      title: groupName,
      value: String(groupId)
    }
  })
}

export function normalizeCategoriesAndGroups(categories: Categories): GroupedItemsList {
  const parentCategories = getParentCategories(categories)
  const normalizedParentCategories = parentCategories.map((parentCategory) => {
    return {
      groupName: parentCategory.groupName,
      items: getItemsForCategory(parentCategory.categories as Category[])
    }
  })

  const groups = getGroups(categories)
  const normalizedGroups =
    groups.length >= 1
      ? [
          {
            groupName: 'Groups',
            items: getItemsForGroups(groups)
          }
        ]
      : []

  return [...normalizedParentCategories, ...normalizedGroups]
}

export function getCategoryMetadata(normalisedCategories: GroupedItemsList, categoryId: number) {
  const category = normalisedCategories.find((groupedItem) => {
    return groupedItem.items.find((item: Item) => item.id === String(categoryId))
  })
  const forum = category?.items.find((item: Item) => item.id === String(categoryId))
  return {
    category: category?.groupName,
    forum: forum?.title
  }
}

export function getFormattedPollData(
  options: string[],
  votes: number[]
): Array<{ option: string, vote: number, percentage: number }> {
  const votesTotal: number = votes.reduce((a, b) => a + b, 0)
  const zippedData = zip(options, votes)
  return Object.values(zippedData).map(([option, vote]) => ({
    option: option,
    vote: vote,
    percentage: isNaN((vote / votesTotal) * 100) ? 0 : Number(((vote / votesTotal) * 100).toFixed())
  }))
}

export function getFormattedVotesData(votes: { [key: string]: number }) {
  return {
    options: Object.keys(votes),
    voteCount: Object.values(votes)
  }
}

export function filterSelectedReplies(replies: string[], selectedReplyId: string | undefined) {
  if (selectedReplyId === undefined) return []
  return replies.filter((id: string) => selectedReplyId !== id)
}

export function filterReplyForBulkAction(contentType: ContentType, topics?: Reply[]) {
  return topics?.map((object: Reply) => {
    return {
      ...object,
      contentType: contentType,
      replyId: object.id
    }
  })
}

export function getPostOffset(publicReplyId: string | undefined): number {
  if (publicReplyId) {
    return (
      window.scrollY +
      (document.querySelector(`[data-public-reply-id='${publicReplyId}']`)?.getBoundingClientRect()?.top ?? 0)
    )
  }
  return 0
}

export function toggleReplyOnTrash(replies: Reply[], replyId: string) {
  return replies.map((reply: Reply) => {
    if (reply.id === replyId) {
      return {
        ...reply,
        trashed: !reply.trashed
      }
    }
    return reply
  })
}

export function getRepliesPaginationParamsFromURL(): { page?: number, pageSize?: number } {
  const searchParams = new URLSearchParams(window.location.search)
  const page = searchParams.has('page') ? Number(searchParams.get('page')) : undefined
  const pageSize = searchParams.has('size') ? Number(searchParams.get('size')) : undefined
  return { page, pageSize }
}

export const contentTypeMapping: Record<ContentType, string> = {
  discussion: 'conversations',
  productUpdate: 'product-updates',
  idea: 'ideas',
  question: 'questions',
  article: 'articles',
  conversation: 'conversations'
}

export const normalizeAuthor = (author: AuthorFromServer | Author): Author => {
  return {
    ...author,
    signature: author.signature === '' ? null : author.signature,
    customTitle: author.customTitle === '' ? null : author.customTitle,
    reputation: {
      ...author.reputation,
      rank: author.reputation.rank === '' ? null : author.reputation.rank,
      rankIcon: author.reputation.rankIcon === '' ? null : author.reputation.rankIcon,
      rankIconThumbnail: author.reputation.rankIconThumbnail === '' ? null : author.reputation.rankIconThumbnail
    }
  }
}

export const ConfigContext = createContext<Config>({
  contentTypeConfig: {
    assignModerator: false,
    bestAnswer: false,
    changeAuthor: false,
    convertToArticle: false,
    convertToConversation: false,
    convertToIdea: false,
    convertToProductUpdate: false,
    convertToQuestion: false,
    copyTopic: false,
    featuredImage: false,
    forum: false,
    ideaStatus: false,
    like: false,
    markAsUnread: false,
    moderationLabel: false,
    moveReplyToExistingTopic: false,
    moveReplyToNewTopic: false,
    moveTopicToExistingTopic: false,
    pending: false,
    pendingReply: false,
    pinReply: false,
    poll: false,
    productAreas: false,
    publicLabel: false,
    spam: false,
    spamReply: false,
    sticky: false,
    trash: false,
    trashReply: false,
    versionHistory: false,
    vote: false
  },
  moderationConfig: {
    contentHelpfulness: false,
    escalate: false,
    escalateReply: false,
    subscribe: false
  }
})
